import React, { useState } from 'react';
import { AlertTriangle, ArrowUpRight, OctagonAlert, X } from 'lucide-react';
import { useTrialStatus } from '../hooks/useTrialStatus';
import { getTrialAlert, type TrialAlert, type TrialAlertLevel } from '../lib/trialAlert';

/** Per tab: a dismissed warning stays hidden until the browser tab closes. */
const DISMISSED_KEY = 'vina_trial_alert_dismissed';

const readDismissed = (): TrialAlertLevel | null => {
  try {
    return window.sessionStorage.getItem(DISMISSED_KEY) as TrialAlertLevel | null;
  } catch {
    return null;
  }
};

const writeDismissed = (level: TrialAlertLevel) => {
  try {
    window.sessionStorage.setItem(DISMISSED_KEY, level);
  } catch {
    // Storage blocked (private mode) — the dismissal just won't survive a reload.
  }
};

/**
 * The card itself stays neutral so it reads in both themes; the level only
 * colours the accent strip, the icon badge and the meter.
 */
const LEVEL_STYLES: Record<TrialAlertLevel, { accent: string; badge: string; meter: string }> = {
  warning: {
    accent: 'bg-[#F59E0B]',
    badge: 'bg-[var(--color-warning-muted)] text-[#D97706]',
    meter: 'bg-[#F59E0B]',
  },
  error: {
    accent: 'bg-[#EF4444]',
    badge: 'bg-[var(--color-error-muted)] text-[#EF4444]',
    meter: 'bg-[#EF4444]',
  },
};

const trialPillLabel = (alert: TrialAlert) =>
  alert.daysLeft > 0
    ? `Free trial · ${alert.daysLeft} ${alert.daysLeft === 1 ? 'day' : 'days'} left`
    : 'Free trial ended';

/**
 * Free-trial alert for CUSTOMER, shown under the page header on every
 * dashboard screen: a warning when the test-message allowance runs low, an
 * error once it is used up or the trial has ended. Renders nothing for every
 * other role (useTrialStatus never fetches for them) and while the trial is
 * comfortably within its limits.
 *
 * It sits below the header rather than above it so the page title stays the
 * first thing on screen; the banner is about the account, not the page.
 *
 * Only the warning can be dismissed. The error means the agent has stopped
 * replying, which the user needs to keep seeing until it is fixed — and since
 * dismissal is stored per level, a dismissed warning doesn't hide the error
 * that follows it.
 */
const TrialLimitBanner: React.FC = () => {
  const alert = getTrialAlert(useTrialStatus());
  const [dismissed, setDismissed] = useState<TrialAlertLevel | null>(readDismissed);

  if (!alert) return null;
  if (alert.level === 'warning' && dismissed === 'warning') return null;

  const styles = LEVEL_STYLES[alert.level];
  const Icon = alert.level === 'error' ? OctagonAlert : AlertTriangle;
  const shownUsed = Math.min(alert.used, alert.max);
  const usedShare = alert.max > 0 ? Math.round((shownUsed / alert.max) * 100) : 100;
  const dismissible = alert.level === 'warning';

  const dismiss = () => {
    writeDismissed('warning');
    setDismissed('warning');
  };

  return (
    <div className="px-7 pt-6 pb-6 max-[900px]:px-4 max-[900px]:pt-4 max-[900px]:pb-4">
      <div
        // An error is announced at once; the warning waits its turn.
        role={alert.level === 'error' ? 'alert' : 'status'}
        className="relative overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--bg-card-flat)] shadow-[0_6px_24px_rgba(111,106,248,0.06)]"
      >
        <span className={`absolute inset-y-0 left-0 w-1 ${styles.accent}`} aria-hidden="true" />

        <div
          className={`flex items-center gap-4 py-4 pl-6 max-[640px]:flex-col max-[640px]:items-stretch ${dismissible ? 'pr-12' : 'pr-5'}`}
        >
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <span
              className={`flex items-center justify-center w-10 h-10 rounded-xl shrink-0 ${styles.badge}`}
              aria-hidden="true"
            >
              <Icon size={20} strokeWidth={2.25} />
            </span>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[var(--text-primary)] font-['Plus_Jakarta_Sans',_sans-serif] text-[15px] font-bold leading-[1.3]">
                  {alert.title}
                </span>
                <span className="inline-flex items-center rounded-full bg-[var(--bg-subtle)] px-2.5 py-0.5 text-[var(--text-secondary)] font-['Plus_Jakarta_Sans',_sans-serif] text-[11px] font-semibold leading-[18px] whitespace-nowrap">
                  {trialPillLabel(alert)}
                </span>
              </div>
              <p className="m-0 mt-1 text-[var(--text-secondary)] font-['Plus_Jakarta_Sans',_sans-serif] text-[13px] leading-[1.5]">
                {alert.message}
              </p>

              <div className="mt-3 flex items-center gap-3">
                <div
                  className="h-1.5 flex-1 max-w-[260px] overflow-hidden rounded-full bg-[var(--border-color)]"
                  role="progressbar"
                  aria-label="Free test messages used"
                  aria-valuemin={0}
                  aria-valuemax={alert.max}
                  aria-valuenow={shownUsed}
                >
                  <div
                    className={`h-full rounded-full transition-[width] duration-500 ease-out ${styles.meter}`}
                    style={{ width: `${usedShare}%` }}
                  />
                </div>
                <span className="text-[var(--text-muted)] font-['Plus_Jakarta_Sans',_sans-serif] text-[12px] font-semibold tabular-nums whitespace-nowrap">
                  {shownUsed} / {alert.max} used
                </span>
              </div>
            </div>
          </div>

          {/* The pricing page is outside the dashboard, so it opens in a new
              tab and the user doesn't lose their place. */}
          <a
            href="/pricing"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-1.5 shrink-0 rounded-xl bg-gradient-brand-btn px-4 py-[10px] text-white no-underline font-['Plus_Jakarta_Sans',_sans-serif] text-[14px] font-bold leading-[normal] hover:opacity-90 max-[640px]:ml-14"
          >
            Upgrade plan
            <ArrowUpRight size={16} aria-hidden="true" />
          </a>
        </div>

        {dismissible && (
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            className="absolute top-3 right-3 flex items-center justify-center w-8 h-8 rounded-lg border-none bg-transparent text-[var(--text-muted)] cursor-pointer hover-bg-app-hover"
          >
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  );
};

export default TrialLimitBanner;
